import { inject } from '@angular/core';
import { NotificationService } from '@iot-platform/notification';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Action } from '@ngrx/store';
import { catchError, concatMap, map, of, tap } from 'rxjs';
import { DeviceVariablesActions } from '../actions';
import { DeviceVariablesService } from '../../services';
import { DeviceVariable, I4BBulkOperationApiResponse, I4BBulkOperationApiResponseStatuses } from '@iot-platform/models/i4b';
import { DevicesFacade, DeviceVariablesFacade } from '../facades';

const loadDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.loadDeviceVariables),
      concatMap(({ deviceId }) =>
        deviceVariablesService.getDeviceVariables(deviceId).pipe(
          map((variables: DeviceVariable[]) => DeviceVariablesActions.loadDeviceVariablesSuccess({ response: variables })),
          catchError((error) => of(DeviceVariablesActions.loadDeviceVariablesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const resetDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.resetDeviceVariablesLastValue),
      concatMap(({ variablesIds }) =>
        deviceVariablesService.bulkResetDeviceVariablesLastValues(variablesIds).pipe(
          map((response: I4BBulkOperationApiResponse) => DeviceVariablesActions.resetDeviceVariablesLastValueSuccess({ response })),
          catchError((error) => of(DeviceVariablesActions.resetDeviceVariablesLastValueFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const deleteDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesService = inject(DeviceVariablesService)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.deleteDeviceVariables),
      concatMap(({ variablesIds }) =>
        deviceVariablesService.bulkDeleteDeviceVariables(variablesIds).pipe(
          map((response: I4BBulkOperationApiResponse) => DeviceVariablesActions.deleteDeviceVariablesSuccess({ response })),
          catchError((error) => of(DeviceVariablesActions.deleteDeviceVariablesFailure({ error })))
        )
      )
    ),
  { functional: true }
);

const reloadDeviceVariables$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), deviceVariablesFacade = inject(DeviceVariablesFacade), devicesFacade = inject(DevicesFacade)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.resetDeviceVariablesLastValueSuccess, DeviceVariablesActions.deleteDeviceVariablesSuccess),
      tap(() => deviceVariablesFacade.loadDeviceVariables(devicesFacade.selectedEntity()?.id))
    ),
  { functional: true, dispatch: false }
);

const showLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.loadDeviceVariables, DeviceVariablesActions.resetDeviceVariablesLastValue, DeviceVariablesActions.deleteDeviceVariables),
      tap(() => notificationService.showLoader())
    ),
  { functional: true, dispatch: false }
);

const hideLoader$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DeviceVariablesActions.loadDeviceVariablesSuccess,
        DeviceVariablesActions.loadDeviceVariablesFailure,
        DeviceVariablesActions.resetDeviceVariablesLastValueSuccess,
        DeviceVariablesActions.resetDeviceVariablesLastValueFailure,
        DeviceVariablesActions.deleteDeviceVariablesSuccess,
        DeviceVariablesActions.deleteDeviceVariablesFailure
      ),
      tap(() => notificationService.hideLoader())
    ),
  { functional: true, dispatch: false }
);

const displaySuccess$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(DeviceVariablesActions.deleteDeviceVariablesSuccess, DeviceVariablesActions.resetDeviceVariablesLastValueSuccess),
      tap(({ response, type }) => notificationService.displaySuccess(type + ' [ ' + I4BBulkOperationApiResponseStatuses[response.status] + ' ] '))
    ),
  { functional: true, dispatch: false }
);

const displayError$ = createEffect(
  /* istanbul ignore next */
  (actions$ = inject(Actions), notificationService = inject(NotificationService)) =>
    actions$.pipe(
      ofType(
        DeviceVariablesActions.loadDeviceVariablesFailure,
        DeviceVariablesActions.resetDeviceVariablesLastValueFailure,
        DeviceVariablesActions.deleteDeviceVariablesFailure
      ),
      tap((action: Action) => notificationService.displayError(action))
    ),
  { functional: true, dispatch: false }
);

export const DeviceVariablesEffects = {
  loadDeviceVariables$,
  resetDeviceVariables$,
  deleteDeviceVariables$,
  reloadDeviceVariables$,
  showLoader$,
  hideLoader$,
  displaySuccess$,
  displayError$
};
